import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ChevronDown } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import SEO from "../components/SEO";
import LandingNav from "../components/Layout/LandingNav";
import LandingFooter from "../components/Layout/LandingFooter";
import { PLANS, TRIAL_DAYS, TRIAL_PLAN, annualMonthlyPrice, annualSavingsPercent, PRICE_RANGE } from "../config/plans";
import { trackPlanSelect, track, EVENTS } from "../utils/analytics";

// Buyer-oriented framing per plan. Numbers come from src/config/plans.js — only the
// positioning copy lives here.
const PLAN_COPY = {
    starter: {
        forWho: "Testing the water, or a very small site",
        blurb: "Enough to put a real chatbot on a small site and see whether visitors use it.",
        extras: ["Custom logo, colours and widget styling", "Light and dark mode", "Email support"],
    },
    basic: {
        forWho: "A growing small business",
        blurb: "The plan most businesses land on. Adds the analytics you need to see what customers actually ask.",
        extras: [
            "Conversation analytics",
            "Transcript export (CSV)",
            "Custom logo, colours and widget styling",
            "Priority email support",
        ],
    },
    pro: {
        forWho: "Higher-volume support",
        blurb: "For sites where the chatbot is carrying real support load and you want it fully white-labelled.",
        extras: [
            "Remove the \"Powered by PaperBrainz\" footer",
            "Advanced analytics (top questions, trends)",
            "Transcript export (CSV)",
            "Priority onboarding support",
        ],
    },
};

// Kept deliberately short and objection-focused. Every answer must match the product.
const PRICING_FAQ = [
    {
        q: "What counts as a conversation?",
        a: "A conversation is one chat session with one visitor, closed after 30 minutes of inactivity. If a visitor asks five questions in a row, that is one conversation — not five.",
    },
    {
        q: `How does the ${TRIAL_DAYS}-day free trial work?`,
        a: `You get ${TRIAL_DAYS} days of the Basic plan free, with no credit card required to start. Your chatbot goes live on your site straight away with full Basic features. Add a payment method from your dashboard before day ${TRIAL_DAYS} to keep it running. Do nothing and it simply stops — you are never charged. Starter and Pro are direct purchase.`,
    },
    {
        q: "What happens if I go over my conversation limit?",
        a: "The chatbot stops answering new conversations until the next billing period or until you move to a larger plan. Nothing is charged automatically for going over, and no overage fees are added.",
    },
    {
        q: "Can I change plans later?",
        a: "Yes, up or down, from the billing page in your dashboard. Changes take effect on your subscription immediately.",
    },
    {
        q: "Can I cancel anytime?",
        a: "Yes. No contracts and no cancellation fee. You cancel yourself from the dashboard, and access continues to the end of the period you have already paid for.",
    },
    {
        q: "Do you take payment details securely?",
        a: "Payments are handled by Lemon Squeezy, which acts as merchant of record and handles billing, invoicing and sales tax. Card details are entered on their checkout and never reach PaperBrainz servers.",
    },
];

function PricingPage() {
    const { theme } = useTheme();
    const d = theme === "dark";
    const { user } = useAuth();
    const navigate = useNavigate();
    const [isAnnual, setIsAnnual] = useState(true);
    const [openFaq, setOpenFaq] = useState(null);

    const savings = annualSavingsPercent(PLANS.find((p) => p.slug === "basic"));

    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Home", item: "https://paperbrainz.com/" },
                    { "@type": "ListItem", position: 2, name: "Pricing", item: "https://paperbrainz.com/pricing" },
                ],
            },
            {
                "@type": "Product",
                name: "PaperBrainz",
                description:
                    "AI customer support chatbot for small business websites. Trained on your own documents and website content.",
                brand: { "@type": "Brand", name: "PaperBrainz" },
                offers: PLANS.map((plan) => ({
                    "@type": "Offer",
                    name: plan.name,
                    price: String(plan.monthlyPrice),
                    priceCurrency: "USD",
                    url: "https://paperbrainz.com/pricing",
                    availability: "https://schema.org/InStock",
                })),
            },
            {
                "@type": "FAQPage",
                mainEntity: PRICING_FAQ.map((item) => ({
                    "@type": "Question",
                    name: item.q,
                    acceptedAnswer: { "@type": "Answer", text: item.a },
                })),
            },
        ],
    };

    const startPlan = (slug) => {
        const billing = isAnnual ? "annual" : "monthly";
        trackPlanSelect(slug, billing, "pricing_page");
        navigate(user ? `/dashboard/billing?autoCheckout=${slug}&billing=${billing}` : `/register?plan=${slug}&billing=${billing}`);
    };

    return (
        <div className={`min-h-screen flex flex-col ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
            <SEO
                title="Pricing"
                description={`AI customer support chatbot pricing from $${PRICE_RANGE.low}/month. ${TRIAL_DAYS}-day free trial on Basic, no credit card required. Conversation limits, plan comparison and answers to common billing questions.`}
                path="/pricing"
                jsonLd={jsonLd}
            />
            <LandingNav />

            <div className="flex-1 pt-32 sm:pt-40 pb-24 sm:pb-32 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-14 max-w-2xl mx-auto">
                        <h1 className={`text-5xl md:text-6xl font-semibold tracking-tight mb-5 ${d ? "text-white" : "text-gray-900"}`}>
                            Pricing
                        </h1>
                        <p className={`text-lg md:text-xl font-light leading-relaxed ${d ? "text-gray-500" : "text-gray-500"}`}>
                            Plans from ${PRICE_RANGE.low} a month. Try Basic free for {TRIAL_DAYS} days — no credit card. Change or cancel
                            from your dashboard whenever you want.
                        </p>
                    </div>

                    {/* Billing toggle */}
                    <div className="flex items-center justify-center mb-12">
                        <div className={`relative flex items-center rounded-full p-1 border ${d ? "bg-white/[0.03] border-white/[0.06]" : "bg-gray-100 border-gray-200"}`}>
                            <button
                                onClick={() => setIsAnnual(false)}
                                className={`relative z-10 px-5 py-2 rounded-full text-sm font-medium transition-all ${!isAnnual ? (d ? "bg-white text-black" : "bg-black text-white") : d ? "text-gray-400" : "text-gray-500"}`}
                            >
                                Monthly
                            </button>
                            <div className="relative">
                                <span className={`absolute -top-3 right-0 translate-x-1/3 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap z-20 ${d ? "bg-white text-black" : "bg-gray-900 text-white"}`}>
                                    Save {savings}%
                                </span>
                                <button
                                    onClick={() => setIsAnnual(true)}
                                    className={`relative z-10 px-5 py-2 rounded-full text-sm font-medium transition-all ${isAnnual ? (d ? "bg-white text-black" : "bg-black text-white") : d ? "text-gray-400" : "text-gray-500"}`}
                                >
                                    Annual
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Plans */}
                    <div className="grid md:grid-cols-3 gap-5 mb-8">
                        {PLANS.map((plan) => {
                            const copy = PLAN_COPY[plan.slug];
                            const price = isAnnual ? annualMonthlyPrice(plan) : plan.monthlyPrice;
                            return (
                                <div
                                    key={plan.slug}
                                    className={`relative p-6 sm:p-8 rounded-[20px] border flex flex-col ${
                                        plan.popular
                                            ? d
                                                ? "border-white/30 bg-white/[0.04]"
                                                : "border-gray-900 bg-white shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)]"
                                            : d
                                              ? "border-white/[0.06] bg-white/[0.02]"
                                              : "border-gray-200 bg-white"
                                    }`}
                                >
                                    {plan.popular && (
                                        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                                            <span className={`px-4 py-1.5 rounded-full text-xs font-medium ${d ? "bg-white text-black" : "bg-black text-white"}`}>
                                                Most Popular
                                            </span>
                                        </div>
                                    )}

                                    <h2 className={`text-lg font-medium mb-1 ${d ? "text-white" : "text-gray-900"}`}>{plan.name}</h2>
                                    <p className={`text-sm font-light mb-5 ${d ? "text-gray-500" : "text-gray-500"}`}>{copy.forWho}</p>

                                    <div className="flex items-baseline mb-1">
                                        <span className={`text-5xl font-semibold tracking-tight tabular-nums ${d ? "text-white" : "text-gray-900"}`}>
                                            ${price}
                                        </span>
                                        <span className={`ml-2 text-sm font-light ${d ? "text-gray-500" : "text-gray-500"}`}>/month</span>
                                    </div>
                                    {isAnnual && (
                                        <p className={`text-sm font-light mb-5 ${d ? "text-gray-600" : "text-gray-400"}`}>
                                            ${plan.annualTotal} billed annually
                                        </p>
                                    )}
                                    {!isAnnual && <div className="mb-5" />}

                                    <p className={`text-sm font-light leading-relaxed mb-6 ${d ? "text-gray-400" : "text-gray-600"}`}>{copy.blurb}</p>

                                    <ul className="space-y-3 mb-8">
                                        <li className="flex items-start gap-3">
                                            <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${d ? "text-gray-400" : "text-gray-500"}`} />
                                            <span className={`text-sm font-light ${d ? "text-gray-300" : "text-gray-700"}`}>
                                                <strong className="font-medium">{plan.limits.conversations.toLocaleString()}</strong> conversations/month
                                            </span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${d ? "text-gray-400" : "text-gray-500"}`} />
                                            <span className={`text-sm font-light ${d ? "text-gray-400" : "text-gray-600"}`}>
                                                {plan.limits.sources === -1 ? "Unlimited knowledge sources" : `Up to ${plan.limits.sources} knowledge sources`}
                                            </span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${d ? "text-gray-400" : "text-gray-500"}`} />
                                            <span className={`text-sm font-light ${d ? "text-gray-400" : "text-gray-600"}`}>
                                                {plan.limits.characters.toLocaleString()} characters of content
                                            </span>
                                        </li>
                                        {copy.extras.map((f) => (
                                            <li key={f} className="flex items-start gap-3">
                                                <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${d ? "text-gray-400" : "text-gray-500"}`} />
                                                <span className={`text-sm font-light ${d ? "text-gray-400" : "text-gray-600"}`}>{f}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="mt-auto">
                                        <button
                                            onClick={() => startPlan(plan.slug)}
                                            className={`w-full py-3 rounded-full font-medium text-sm transition-all ${
                                                plan.popular
                                                    ? d
                                                        ? "bg-white text-black hover:bg-gray-200"
                                                        : "bg-black text-white hover:bg-gray-800"
                                                    : d
                                                      ? "text-white border border-white/[0.08] hover:bg-white/[0.04]"
                                                      : "text-gray-900 border border-gray-200 hover:bg-gray-50"
                                            }`}
                                        >
                                            {plan.trial ? `Start ${TRIAL_DAYS}-day free trial` : `Get ${plan.name}`}
                                        </button>
                                        {plan.slug === TRIAL_PLAN && (
                                            <p className={`text-xs font-light text-center mt-3 ${d ? "text-gray-600" : "text-gray-400"}`}>
                                                No credit card required
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Comparison table */}
                    <div className="mb-20 overflow-x-auto">
                        <table className={`w-full min-w-[560px] text-sm border-collapse ${d ? "text-gray-400" : "text-gray-600"}`}>
                            <caption className="sr-only">Feature comparison across PaperBrainz plans</caption>
                            <thead>
                                <tr className={`border-b ${d ? "border-white/[0.08]" : "border-gray-200"}`}>
                                    <th scope="col" className={`text-left py-3 pr-4 font-medium ${d ? "text-white" : "text-gray-900"}`}>Feature</th>
                                    {PLANS.map((p) => (
                                        <th key={p.slug} scope="col" className={`text-left py-3 px-4 font-medium ${d ? "text-white" : "text-gray-900"}`}>
                                            {p.name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="font-light">
                                {[
                                    ["Conversations per month", (p) => p.limits.conversations.toLocaleString()],
                                    ["Knowledge sources", (p) => (p.limits.sources === -1 ? "Unlimited" : p.limits.sources)],
                                    ["Content characters", (p) => p.limits.characters.toLocaleString()],
                                    ["Custom logo and colours", () => "Yes"],
                                    ["Conversation analytics", (p) => (p.slug === "starter" ? "—" : "Yes")],
                                    ["Transcript export (CSV)", (p) => (p.slug === "starter" ? "—" : "Yes")],
                                    ["Advanced analytics", (p) => (p.slug === "pro" ? "Yes" : "—")],
                                    ["Remove PaperBrainz branding", (p) => (p.slug === "pro" ? "Yes" : "—")],
                                    ["Free trial", (p) => (p.trial ? `${TRIAL_DAYS} days` : "—")],
                                ].map(([label, get]) => (
                                    <tr key={label} className={`border-b ${d ? "border-white/[0.04]" : "border-gray-100"}`}>
                                        <th scope="row" className="text-left py-3 pr-4 font-light">{label}</th>
                                        {PLANS.map((p) => (
                                            <td key={p.slug} className="py-3 px-4">{get(p)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* FAQ */}
                    <div className="max-w-2xl mx-auto">
                        <h2 className={`text-3xl md:text-4xl font-semibold tracking-tight text-center mb-10 ${d ? "text-white" : "text-gray-900"}`}>
                            Before you buy
                        </h2>
                        <div className="space-y-3">
                            {PRICING_FAQ.map((faq, i) => (
                                <div key={faq.q} className={`rounded-[16px] border overflow-hidden ${d ? "border-white/[0.06]" : "border-gray-200"}`}>
                                    <button
                                        onClick={() => {
                                            if (openFaq !== i) track(EVENTS.FAQ_OPEN, { question_index: i + 1, page: "pricing" });
                                            setOpenFaq(openFaq === i ? null : i);
                                        }}
                                        className="w-full flex items-center justify-between p-5 text-left"
                                    >
                                        <span className={`font-medium pr-4 text-[15px] ${d ? "text-white" : "text-gray-900"}`}>{faq.q}</span>
                                        <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""} ${d ? "text-gray-600" : "text-gray-500"}`} />
                                    </button>
                                    <div className={`overflow-hidden transition-all duration-200 ${openFaq === i ? "max-h-96 pb-5" : "max-h-0"}`}>
                                        <p className={`px-5 text-sm leading-relaxed font-light ${d ? "text-gray-500" : "text-gray-500"}`}>{faq.a}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className={`text-center text-sm font-light mt-10 ${d ? "text-gray-500" : "text-gray-500"}`}>
                            Still deciding? Read how we handle your data on our{" "}
                            <Link to="/security" className="underline hover:no-underline">security page</Link>, or{" "}
                            <a href="mailto:support@paperbrainz.com" className="underline hover:no-underline">email us</a>.
                        </p>
                    </div>
                </div>
            </div>

            <LandingFooter />
        </div>
    );
}

export default PricingPage;
